import React, { useState, useEffect, useMemo } from "react";
import { Modal, Space, Input, Button, Alert, Typography, Spin, Progress, Divider, Row, Col, Tag, message, Card } from "antd";
import { ScissorOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined, FileOutlined, EyeOutlined } from "@ant-design/icons";
import { Document, Page } from "react-pdf";
import { PDFDocument } from "pdf-lib";
import PDFPagePreview from "./PDFPagePreview";
import { getPresignedViewUrl } from "../../../../api/attachmentApi";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// 配置 PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const { Text, Title } = Typography;
const { TextArea } = Input;

// 样式常量
const MODAL_STYLES = {
    content: {
        backgroundColor: "#f5f5f5",
        padding: "16px 24px",
        minHeight: "500px",
    },
    previewContainer: {
        maxHeight: 580,
        overflowY: "auto",
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        padding: 12,
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    },
    configContainer: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    previewItemsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: 16,
        padding: 8,
    },
    splitPreviewContainer: {
        maxHeight: 280,
        overflowY: "auto",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 12,
        backgroundColor: "#fafafa",
    },
    splitPreviewItem: {
        padding: "12px 16px",
        marginBottom: 10,
        backgroundColor: "#fff",
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        transition: "all 0.3s",
        cursor: "default",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
    },
};

/**
 * PDF文件拆分弹窗组件
 * 支持页面范围输入、实时预览、浏览器内拆分、自动上传
 */
const FileSplitModal = ({
    open,
    file, // { storage_path, filename, url }
    onConfirm, // (originalFile, subFiles) => void
    onCancel,
}) => {
    const [numPages, setNumPages] = useState(0);
    const [pdfData, setPdfData] = useState(null);
    const [signedPdfUrl, setSignedPdfUrl] = useState(null); // 签名后的OSS访问链接
    const [pageRanges, setPageRanges] = useState(""); // 用户输入的页面范围，如 "1-3, 4-10"
    const [isLoading, setIsLoading] = useState(false);
    const [isSplitting, setIsSplitting] = useState(false);
    const [splitProgress, setSplitProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [previewPage, setPreviewPage] = useState(null); // 当前预览的页码

    // 加载PDF文件
    useEffect(() => {
        if (open && file) {
            const loadPdf = async () => {
                setIsLoading(true);
                setErrorMessage("");
                setPageRanges("");
                setSplitProgress(0);
                setSignedPdfUrl(null);

                try {
                    // ========== V4.5 优化：支持本地File对象 ==========
                    if (file.rawFile instanceof File || file.rawFile instanceof Blob) {
                        // 本地文件：直接读取，不需要网络请求
                        console.log("[FileSplitModal] 使用本地File对象:", file.filename || file.rawFile.name);
                        const arrayBuffer = await file.rawFile.arrayBuffer();
                        setPdfData(arrayBuffer);
                        // 为预览创建Object URL
                        const objectUrl = URL.createObjectURL(file.rawFile);
                        setSignedPdfUrl(objectUrl);
                    } else {
                        // OSS文件：从网络获取
                        // 1. 获取签名 URL（如果支持）
                        let urlToUse = file.download_url || file.url;

                        // 如果有 storage_path，优先尝试获取最新的签名 URL
                        if (file.storage_path) {
                            try {
                                const res = await getPresignedViewUrl(file.storage_path);
                                if (res && res.signed_url) {
                                    urlToUse = res.signed_url;
                                }
                            } catch (err) {
                                console.warn("获取签名 URL 失败，将尝试使用原始 URL:", err);
                            }
                        } else if (!urlToUse && file.object_key) {
                            // 兼容某些已有数据的字段名
                            try {
                                const res = await getPresignedViewUrl(file.object_key);
                                if (res && res.signed_url) {
                                    urlToUse = res.signed_url;
                                }
                            } catch (err) {
                                console.warn("获取签名 URL 失败:", err);
                            }
                        }

                        if (!urlToUse) {
                            throw new Error("无法获取 PDF 文件 URL，请确保文件包含 storage_path 或 download_url");
                        }

                        setSignedPdfUrl(urlToUse);

                        // 2. 通过 fetch 获取 PDF 文件二进制数据
                        const response = await fetch(urlToUse);
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }
                        const arrayBuffer = await response.arrayBuffer();
                        setPdfData(arrayBuffer);
                    }
                } catch (error) {
                    console.error("PDF 加载失败:", error);
                    setErrorMessage(`PDF 文件加载失败: ${error.message}`);
                } finally {
                    setIsLoading(false);
                }
            };

            loadPdf();
        }

        // 清理 Object URL
        return () => {
            if (signedPdfUrl && signedPdfUrl.startsWith("blob:")) {
                URL.revokeObjectURL(signedPdfUrl);
            }
        };
    }, [open, file]);

    // PDF文档加载成功回调
    const onDocumentLoadSuccess = ({ numPages: totalPages }) => {
        setNumPages(totalPages);
    };

    // PDF文档加载失败回调
    const onDocumentLoadError = (error) => {
        console.error("PDF文档加载失败:", error);
        setErrorMessage(`PDF文档加载失败: ${error.message || "未知错误"}`);
    };

    // 解析用户输入的页面范围
    const parsedRanges = useMemo(() => {
        if (!pageRanges.trim() || numPages === 0) {
            return [];
        }

        const ranges = [];
        // 支持中英文分隔符：英文逗号(,) 和 中文逗号(，)
        const parts = pageRanges
            .split(/[,，]/)
            .map((p) => p.trim())
            .filter(Boolean);

        for (const part of parts) {
            // 支持中英文连字符：英文连字符(-) 和 中文连字符(—、～、~、–)
            const rangeMatch = part.match(/^(\d+)\s*[-—～~–]\s*(\d+)$/);

            if (rangeMatch) {
                // 范围格式：如 "1-5" 或 "1—5" 或 "1～5"
                const start = parseInt(rangeMatch[1], 10);
                const end = parseInt(rangeMatch[2], 10);
                if (isNaN(start) || isNaN(end) || start < 1 || end > numPages || start > end) {
                    return null; // 无效范围
                }
                ranges.push({ start, end });
            } else {
                // 单页格式：如 "5"
                const page = parseInt(part, 10);
                if (isNaN(page) || page < 1 || page > numPages) {
                    return null; // 无效页码
                }
                ranges.push({ start: page, end: page });
            }
        }

        // 注意：支持页面范围重叠（如"1-3, 3-4"），因为实际业务中某一页可能包含两个业务的内容
        // 不再检查范围重叠

        // 统计所有被拆分的页面（用于可视化，允许重复）
        const allPages = new Set();
        ranges.forEach(({ start, end }) => {
            for (let p = start; p <= end; p++) {
                allPages.add(p);
            }
        });

        return ranges.sort((a, b) => a.start - b.start);
    }, [pageRanges, numPages]);

    // 预览拆分结果
    const splitPreview = useMemo(() => {
        if (!parsedRanges || parsedRanges.length === 0) {
            return [];
        }

        const baseFilename = file?.filename?.replace(/\.pdf$/i, "") || "文件";

        return parsedRanges.map((range, index) => {
            const pageCount = range.end - range.start + 1;
            const rangeStr = range.start === range.end ? `p${range.start}` : `p${range.start}-${range.end}`;
            return {
                index: index + 1,
                filename: `${baseFilename}_${rangeStr}.pdf`,
                pageRange: `${range.start}-${range.end}`,
                pageCount,
                start: range.start,
                end: range.end,
            };
        });
    }, [parsedRanges, file]);

    // 执行PDF拆分
    const handleSplit = async () => {
        if (!parsedRanges || parsedRanges.length === 0) {
            message.error("请输入有效的页面范围");
            return;
        }

        if (parsedRanges.length < 2) {
            message.error("拆分至少需要生成2个文件");
            return;
        }

        setIsSplitting(true);
        setSplitProgress(0);
        setErrorMessage("");

        try {
            // 1. 获取PDF数据
            let freshArrayBuffer;
            if (file.rawFile instanceof File || file.rawFile instanceof Blob) {
                // 本地文件：直接读取
                freshArrayBuffer = await file.rawFile.arrayBuffer();
            } else if (signedPdfUrl) {
                // OSS文件：通过fetch获取
                const response = await fetch(signedPdfUrl);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                freshArrayBuffer = await response.arrayBuffer();
            } else {
                throw new Error("无法获取PDF文件数据");
            }

            // 2. 加载原始PDF文档
            const pdfDoc = await PDFDocument.load(freshArrayBuffer);
            setSplitProgress(10);

            const subFiles = [];
            const totalFiles = parsedRanges.length;

            // 3. 为每个范围创建子PDF
            for (let i = 0; i < parsedRanges.length; i++) {
                const range = parsedRanges[i];
                const { start, end } = range;

                // 创建新的PDF文档
                const newPdf = await PDFDocument.create();

                // 复制页面 (PDF页面索引从0开始)
                const pages = await newPdf.copyPages(
                    pdfDoc,
                    Array.from({ length: end - start + 1 }, (_, idx) => start - 1 + idx),
                );

                pages.forEach((page) => newPdf.addPage(page));

                // 保存为字节数组
                const pdfBytes = await newPdf.save();

                // 生成文件名
                const baseFilename = file.filename.replace(/\.pdf$/i, "");
                const rangeStr = start === end ? `p${start}` : `p${start}-${end}`;
                const newFilename = `${baseFilename}_${rangeStr}.pdf`;

                // 创建Blob对象
                const blob = new Blob([pdfBytes], { type: "application/pdf" });
                const newFile = new File([blob], newFilename, { type: "application/pdf" });

                subFiles.push({
                    file: newFile,
                    filename: newFilename,
                    pageRange: `${start}-${end}`,
                    pageCount: end - start + 1,
                    size: blob.size,
                });

                // 更新进度
                setSplitProgress(10 + ((i + 1) / totalFiles) * 90);
            }

            setSplitProgress(100);

            // 4. 回调通知父组件
            if (onConfirm) {
                onConfirm(file, subFiles);
            }

            message.success(`成功拆分为 ${subFiles.length} 个文件`);
            handleClose();
        } catch (error) {
            console.error("PDF拆分失败:", error);
            setErrorMessage(`拆分失败: ${error.message || "未知错误"}`);
            message.error("PDF拆分失败，请检查文件格式");
        } finally {
            setIsSplitting(false);
        }
    };

    // 关闭弹窗
    const handleClose = () => {
        if (!isSplitting && onCancel) {
            onCancel();
        }
    };

    // 输入验证提示
    const getInputStatus = () => {
        if (!pageRanges.trim()) {
            return { status: "default", message: null };
        }
        if (parsedRanges === null) {
            return {
                status: "error",
                message: "页面范围格式错误或超出范围，请检查输入",
            };
        }
        if (parsedRanges.length < 2) {
            return {
                status: "warning",
                message: "至少需要拆分为2个文件",
            };
        }
        return {
            status: "success",
            message: `将拆分为 ${parsedRanges.length} 个文件`,
        };
    };

    const inputStatus = getInputStatus();
    const canSplit = parsedRanges && parsedRanges.length >= 2 && !isSplitting;

    return (
        <Modal
            title={
                <Space size={12}>
                    <ScissorOutlined style={{ fontSize: 18, color: "#1890ff" }} />
                    <span style={{ fontSize: 16, fontWeight: 500 }}>拆分PDF文件</span>
                    {file && (
                        <Tag icon={<FileOutlined />} color="blue" style={{ marginLeft: 8 }}>
                            {file.filename}
                        </Tag>
                    )}
                </Space>
            }
            open={open}
            onCancel={handleClose}
            width={1200}
            centered
            className="pdf-split-modal"
            bodyStyle={MODAL_STYLES.content}
            footer={[
                <Button key="cancel" onClick={handleClose} disabled={isSplitting} size="large">
                    取消
                </Button>,
                <Button key="split" type="primary" onClick={handleSplit} disabled={!canSplit} loading={isSplitting} icon={<ScissorOutlined />} size="large">
                    {isSplitting ? "拆分中..." : "确认拆分"}
                </Button>,
            ]}
            maskClosable={false}
            destroyOnHidden
        >
            {/* 错误信息 */}
            {errorMessage && <Alert message="操作失败" description={errorMessage} type="error" closable showIcon onClose={() => setErrorMessage("")} style={{ marginBottom: 16 }} />}

            {/* 加载状态 */}
            {isLoading && (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spin size="large" tip="正在加载PDF文件...">
                        <div style={{ minHeight: 100 }} />
                    </Spin>
                </div>
            )}

            {/* 拆分进度 */}
            {isSplitting && (
                <div style={{ marginBottom: 16 }}>
                    <Progress percent={Math.round(splitProgress)} status="active" />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        正在拆分PDF文件，请稍候...
                    </Text>
                </div>
            )}

            {/* PDF预览和拆分配置 */}
            {!isLoading && pdfData && !isSplitting && (
                <Row gutter={24}>
                    {/* 左侧：页面预览 */}
                    <Col span={11}>
                        <Card
                            size="small"
                            title={
                                <Space>
                                    <FileOutlined />
                                    <span>页面预览</span>
                                    {numPages > 0 && (
                                        <Tag color="blue" style={{ marginLeft: 4 }}>
                                            共 {numPages} 页
                                        </Tag>
                                    )}
                                </Space>
                            }
                            bordered={false}
                            bodyStyle={{ padding: 0 }}
                        >
                            <div style={MODAL_STYLES.previewContainer}>
                                <Document
                                    file={pdfData}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={onDocumentLoadError}
                                    loading={
                                        <div style={{ textAlign: "center", padding: "20px 0" }}>
                                            <Spin size="small" tip="加载页面...">
                                                <div style={{ minHeight: 50 }} />
                                            </Spin>
                                        </div>
                                    }
                                >
                                    {numPages > 0 && Array.from({ length: numPages }, (_, index) => <PDFPagePreview key={index + 1} pageNumber={index + 1} width={170} onClick={(page) => setPreviewPage(page)} />)}
                                </Document>
                            </div>
                        </Card>
                    </Col>

                    {/* 右侧：拆分配置 */}
                    <Col span={13}>
                        <Card
                            size="small"
                            title={
                                <Space>
                                    <ScissorOutlined />
                                    <span>拆分方案</span>
                                </Space>
                            }
                            bordered={false}
                            bodyStyle={{ padding: "16px 24px" }}
                        >
                            {numPages > 0 ? (
                                <>
                                    {/* 输入说明 */}
                                    <Alert
                                        message={<span style={{ fontWeight: 500 }}>页面范围格式</span>}
                                        description={
                                            <div style={{ lineHeight: "1.8" }}>
                                                <div>
                                                    • 单页: <Text code>5</Text>
                                                </div>
                                                <div>
                                                    • 范围: <Text code>1-5</Text> 或 <Text code>1—5</Text>
                                                </div>
                                                <div>
                                                    • 多个范围: <Text code>1-5, 6-10</Text> 或 <Text code>1—5，6—10</Text>
                                                </div>
                                                <div>
                                                    • 支持重叠: <Text code>1-3, 3-5</Text>{" "}
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        (某页包含多个业务时适用)
                                                    </Text>
                                                </div>
                                                <div style={{ marginTop: 6 }}>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        💡 支持中英文符号：逗号(,或，)、连字符(-或—或～)
                                                    </Text>
                                                </div>
                                                <div style={{ marginTop: 4 }}>
                                                    <Tag color="blue" icon={<FileOutlined />}>
                                                        共 {numPages} 页可用
                                                    </Tag>
                                                </div>
                                            </div>
                                        }
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />

                                    {/* 拆分快捷工具 */}
                                    <div style={{ marginBottom: 16, padding: "12px", background: "#f9f9f9", borderRadius: "6px", border: "1px solid #f0f0f0" }}>
                                        <div style={{ marginBottom: 8, fontSize: "12px", color: "#666" }}>
                                            <Space>
                                                <ScissorOutlined />
                                                快速拆分工具
                                            </Space>
                                        </div>
                                        <Space wrap>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    const ranges = [];
                                                    for (let i = 1; i <= numPages; i++) {
                                                        ranges.push(`${i}`);
                                                    }
                                                    setPageRanges(ranges.join(", "));
                                                }}
                                            >
                                                每页拆分一张
                                            </Button>
                                            <Input.Group compact style={{ display: "inline-flex", width: "auto" }}>
                                                <Input
                                                    style={{ width: 60, textAlign: "center" }}
                                                    placeholder="N"
                                                    size="small"
                                                    id="split-n-input"
                                                    onKeyDown={(e) => {
                                                        // 阻止非数字输入
                                                        if (!/[0-9]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        const nInput = document.getElementById("split-n-input");
                                                        const n = parseInt(nInput.value);
                                                        if (!n || n < 1) {
                                                            message.warning("请输入有效的页数 N");
                                                            return;
                                                        }
                                                        if (n >= numPages) {
                                                            message.warning("N 必须小于总页数");
                                                            return;
                                                        }

                                                        const ranges = [];
                                                        for (let i = 1; i <= numPages; i += n) {
                                                            const end = Math.min(i + n - 1, numPages);
                                                            ranges.push(`${i}-${end}`);
                                                        }
                                                        setPageRanges(ranges.join(", "));
                                                    }}
                                                >
                                                    每 N 页拆分
                                                </Button>
                                            </Input.Group>
                                        </Space>
                                    </div>

                                    {/* 页面范围输入 */}
                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13 }}>
                                            <ScissorOutlined style={{ marginRight: 6, color: "#1890ff" }} />
                                            拆分方案手动输入
                                        </div>
                                        <TextArea
                                            value={pageRanges}
                                            onChange={(e) => setPageRanges(e.target.value)}
                                            placeholder={`例如: 1-${Math.floor(numPages / 2)}, ${Math.floor(numPages / 2) + 1}-${numPages}\n或: 1—${Math.floor(numPages / 2)}，${Math.floor(numPages / 2) + 1}—${numPages} (支持中文符号)`}
                                            rows={3}
                                            style={{ fontSize: 13 }}
                                            status={inputStatus.status === "error" ? "error" : undefined}
                                        />
                                    </div>

                                    {/* 输入提示 */}
                                    {inputStatus.message && <Alert message={inputStatus.message} type={inputStatus.status === "error" ? "error" : inputStatus.status === "warning" ? "warning" : "success"} showIcon icon={inputStatus.status === "success" ? <CheckCircleOutlined /> : <CloseCircleOutlined />} style={{ marginBottom: 12 }} />}

                                    {/* 拆分预览 */}
                                    {splitPreview.length > 0 && (
                                        <>
                                            <Divider orientation="left" style={{ margin: "16px 0 12px", fontWeight: 500 }}>
                                                拆分预览 ({splitPreview.length} 个文件)
                                            </Divider>
                                            <div style={MODAL_STYLES.splitPreviewContainer}>
                                                {splitPreview.map((item) => (
                                                    <div key={item.index} style={MODAL_STYLES.splitPreviewItem}>
                                                        <Space direction="vertical" size={4} style={{ width: "100%" }}>
                                                            <Space>
                                                                <Tag color="processing">文件 {item.index}</Tag>
                                                                <Text strong style={{ fontSize: 13 }}>
                                                                    {item.filename}
                                                                </Text>
                                                            </Space>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                页面范围: {item.pageRange} · 共 {item.pageCount} 页
                                                            </Text>
                                                        </Space>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div style={{ textAlign: "center", padding: "40px 0" }}>
                                    <Spin tip="正在解析PDF页面...">
                                        <div style={{ minHeight: 100 }} />
                                    </Spin>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            {/* 页面放大预览Modal */}
            <Modal
                title={
                    <Space>
                        <EyeOutlined style={{ color: "#1890ff" }} />
                        <span>页面预览</span>
                        {previewPage && <Tag color="blue">第 {previewPage} 页</Tag>}
                    </Space>
                }
                open={previewPage !== null}
                onCancel={() => setPreviewPage(null)}
                width={900}
                centered
                footer={[
                    <Button key="prev" onClick={() => setPreviewPage(Math.max(1, previewPage - 1))} disabled={previewPage === 1}>
                        上一页
                    </Button>,
                    <Button key="next" onClick={() => setPreviewPage(Math.min(numPages, previewPage + 1))} disabled={previewPage === numPages}>
                        下一页
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setPreviewPage(null)}>
                        关闭
                    </Button>,
                ]}
                bodyStyle={{
                    padding: 24,
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 600,
                }}
            >
                {previewPage && file && (
                    <div
                        style={{
                            backgroundColor: "#fff",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                            borderRadius: 8,
                            overflow: "hidden",
                        }}
                    >
                        {/* 使用URL而不是ArrayBuffer，避免detached问题 */}
                        {/* 使用 signedPdfUrl 确保权限 */}
                        <Document
                            file={signedPdfUrl}
                            loading={
                                <div style={{ width: 800, height: 1131, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Spin size="large" tip="加载中..." />
                                </div>
                            }
                        >
                            <Page pageNumber={previewPage} width={800} renderTextLayer={true} renderAnnotationLayer={true} />
                        </Document>
                    </div>
                )}
            </Modal>
        </Modal>
    );
};

export default FileSplitModal;
